import React, { useEffect, useMemo, useRef, useState } from "react";
import { NodeSelection } from "@tiptap/pm/state";
import { NodeViewWrapper } from "@tiptap/react";
import {
  normalizeFormulaLatex,
  renderKatexFormulaHtml,
} from "../../../utils/renderSavedFormulas";

export default function FormulaBox({ node, selected, editor, getPos, updateAttributes }) {
  const sourceRef = useRef(null);
  const [sourceFocused, setSourceFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const latex = node.attrs?.latex || "";
  const displayMode = !!node.attrs?.displayMode;
  const sourceOpen = selected || sourceFocused;

  const renderedHtml = useMemo(() => {
    const normalized = normalizeFormulaLatex(latex);
    if (!normalized) return "";

    try {
      return renderKatexFormulaHtml(normalized, displayMode);
    } catch {
      return "";
    }
  }, [displayMode, latex]);

  useEffect(() => {
    if (!selected) {
      setMenuOpen(false);
      return;
    }

    if (latex) return;

    const id = requestAnimationFrame(() => {
      try {
        sourceRef.current?.focus();
      } catch {}
    });

    return () => cancelAnimationFrame(id);
  }, [latex, selected]);

  const selectNode = () => {
    const pos = getPos?.();
    if (typeof pos !== "number" || !editor?.view?.dispatch) return;

    const tr = editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos));
    editor.view.dispatch(tr);
  };

  const onFormulaMouseDown = (event) => {
    if (event.target?.closest?.(".lh6-formulaSourceBox")) return;
    if (event.target?.closest?.(".lh6-formulaMenuBtn")) return;
    if (event.target?.closest?.(".lh6-formulaMenu")) return;

    event.preventDefault();
    event.stopPropagation();
    selectNode();
  };

  const setDisplayMode = (nextDisplayMode) => {
    updateAttributes({ displayMode: !!nextDisplayMode });
    setMenuOpen(false);
    selectNode();
  };

  const classes = [
    "lh6-formulaNode",
    "lh6-katexFormula",
    displayMode ? "is-block" : "is-inline",
    selected ? "is-selected" : "",
    sourceOpen ? "is-source-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <NodeViewWrapper
      as="span"
      className={classes}
      data-formula="1"
      data-lh6-formula="1"
      data-display-mode={displayMode ? "1" : "0"}
      data-latex={latex}
      contentEditable={false}
      onMouseDown={onFormulaMouseDown}
    >
      <span className="lh6-formulaTop">
        <span
          className="lh6-formulaRender"
          role="math"
          aria-label={latex || "Leere Formel"}
          dangerouslySetInnerHTML={{
            __html: renderedHtml || '<span class="lh6-formulaPlaceholder">Formel</span>',
          }}
        />
        <span className="lh6-formulaMenuWrap">
          <button
            type="button"
            className="lh6-formulaMenuBtn"
            aria-label="Formelmenü"
            title="Formelmenü"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              selectNode();
              setMenuOpen((prev) => !prev);
            }}
          >
            <span />
            <span />
            <span />
          </button>
          {menuOpen ? (
            <span className="lh6-formulaMenu" role="menu">
              <button
                type="button"
                className={!displayMode ? "is-active" : ""}
                onClick={() => setDisplayMode(false)}
              >
                Inline
              </button>
              <button
                type="button"
                className={displayMode ? "is-active" : ""}
                onClick={() => setDisplayMode(true)}
              >
                Block
              </button>
            </span>
          ) : null}
        </span>
      </span>

      {sourceOpen ? (
        <span className="lh6-formulaSourceBox">
          <textarea
            ref={sourceRef}
            value={latex}
            rows={2}
            spellCheck={false}
            aria-label="KaTeX bearbeiten"
            onChange={(event) => updateAttributes({ latex: event.target.value })}
            onFocus={() => {
              setSourceFocused(true);
              selectNode();
            }}
            onBlur={() => setSourceFocused(false)}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          />
        </span>
      ) : null}
    </NodeViewWrapper>
  );
}
